import React from 'react';

import { AppIcon } from '@backrs_ui/common-ui';

import { GUIDE_TEXT_MAX_WIDTH } from '@backrs_ui/grow-ui/src/components/GuidePage';
import type {
  InternshipGuideMythRealityItem,
  InternshipGuideMythsVsReality,
} from '@backrs_ui/grow-ui/src/pages/InternshipGuide/content/types';

import { Box, SxProps, Theme, Typography } from '@mui/material';

type MythsVsRealitySectionProps = Readonly<{
  content: InternshipGuideMythsVsReality;
}>;

type BadgeVariant = 'myth' | 'reality';

const Badge: React.FC<{ variant: BadgeVariant; label: string }> = ({ variant, label }) => {
  const isMyth = variant === 'myth';

  return (
    <Box sx={isMyth ? mythBadgeSx : realityBadgeSx}>
      <AppIcon
        iconName={isMyth ? 'SlashCircle01' : 'CheckCircle'}
        diameter="14px"
        color={isMyth ? 'Colors/Text/text-error-primary' : 'Colors/Text/text-success-primary'}
      />
      <Typography variant="text-xs-semibold">{label}</Typography>
    </Box>
  );
};

const PairBlock: React.FC<{
  variant: BadgeVariant;
  item: InternshipGuideMythRealityItem;
  label: string;
}> = ({ variant, item, label }) => (
  <Box sx={blockSx}>
    <Badge variant={variant} label={label} />
    <Typography variant="text-md-semibold" sx={blockTitleSx}>
      {item.title}
    </Typography>
    <Typography variant="text-sm-regular" sx={blockBodySx}>
      {item.body}
    </Typography>
  </Box>
);

export const MythsVsRealitySection: React.FC<MythsVsRealitySectionProps> = ({ content }) => (
  <Box sx={containerSx}>
    <Typography variant="text-md-regular" sx={introSx}>
      {content.intro}
    </Typography>

    <Box sx={gridSx}>
      {content.pairs.map((pair) => (
        <React.Fragment key={pair.myth.title}>
          <Box sx={cardSx}>
            <PairBlock variant="myth" item={pair.myth} label={content.mythLabel} />
          </Box>
          <Box sx={cardSx}>
            <PairBlock variant="reality" item={pair.reality} label={content.realityLabel} />
          </Box>
        </React.Fragment>
      ))}
    </Box>
  </Box>
);

const containerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
};

const introSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
  mb: backrs.spacing['spacing-3xl'],
  maxWidth: GUIDE_TEXT_MAX_WIDTH,
});

const gridSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: backrs.spacing['spacing-xl'],
  '@container (min-width: 720px)': {
    gridTemplateColumns: 'repeat(2, 1fr)',
  },
});

const cardSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  p: backrs.spacing['spacing-xl'],
  border: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  borderRadius: backrs.radius['radius-xl'],
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
});

const blockSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-sm'],
  alignItems: 'flex-start',
});

const blockTitleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});

const blockBodySx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const badgeBaseSx = (backrs: Theme['backrs']) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-xs'],
  py: backrs.spacing['spacing-xxs'],
  px: backrs.spacing['spacing-md'],
  borderRadius: backrs.radius['radius-full'],
  border: '1px solid',
});

const mythBadgeSx: SxProps<Theme> = ({ backrs }) => ({
  ...badgeBaseSx(backrs),
  color: backrs.colors['Colors/Text/text-error-primary'],
  backgroundColor: backrs.colors['Colors/Background/bg-error-primary'],
  borderColor: backrs.colors['Colors/Background/bg-error-secondary'],
});

const realityBadgeSx: SxProps<Theme> = ({ backrs }) => ({
  ...badgeBaseSx(backrs),
  color: backrs.colors['Colors/Text/text-success-primary'],
  backgroundColor: backrs.colors['Colors/Background/bg-success-primary'],
  borderColor: backrs.colors['Colors/Background/bg-success-secondary'],
});
